import { html } from 'lit-element';
import { nothing } from 'lit-html';
import { KatLitElement, register } from '../../shared/base';
import { checkSlots } from '../../shared/slot-utils';
import baseStyles from '../../shared/base/base.lit.scss';
import cardStyles from './card.lit.scss';

/**
 * @component {kat-card} KatalCard Cards convey information to the customer in a quick, cohesive, and easy-to-understand manner. A card acts as an entry point to more detailed information in a small, defined area. Diverse types of information are presented in a contextually-cohesive, digestible manner. They may include messaging and imagery, complex data visualizations, and messaging, making features more discoverable.
 * @status Production
 * @theme katal
 * @contentProvided
 * @example Basic {
 * "content": "
 *  <span slot=\"title\">
 *      Title
 *  </span>
 * <kat-button variant=\"link\" label=\"Action\" slot=\"action\">
 *  </kat-button>
 * <span slot=\"subtitle\">
 *      Subtitle
 *  </span>
 *   <div>
 *      Body content
 *  </div>
 * <div slot=\"footer\">
 *      Footer
 *  </div>
 * "}
 *  @example TitleProperty {
 * "title": "Title as Property",
 * "content": "
 * <kat-button variant=\"link\" label=\"Action\"  slot=\"action\">
 *  </kat-button>
 * <span slot=\"subtitle\">
 *      Subtitle
 *  </span>
 *   <div>
 *      Body content
 *  </div>
 * <div slot=\"footer\">
 *      Footer
 *  </div>
 * "}
 *  @example Warning {
 * "variant": "warning",
 * "content": "
 * <span slot=\"title\">Warning</span>
 * <span slot=\"subtitle\">
 *      Subtitle
 *  </span>
 *   <div>
 *      Body content
 *  </div>
 * "}
 *  @example Danger {
 * "variant": "danger",
 * "content": "
 * <span slot=\"title\">Danger</span>
 * <span slot=\"subtitle\">
 *      Subtitle
 *  </span>
 *   <div>
 *      Body content
 *  </div>
 * "}
 * @guideline Do Use Cards when there is a small area in which a large or varying amount of information must be communicated
 * @guideline Do Use simple, succinctly-messaged and clearly-designed information that can be quickly understood
 * @guideline Do Keep the number of cards shown to a manageable amount that does not require scrolling on desktop as too many can be overwhelming
 * @guideline Dont Don't use a Card if the information requires more room to be clearly communicated, or takes several steps to complete
 * @guideline Dont Don't overload a Card with information or messaging, rendering it difficult to understand
 */

/**
 * @slot default Elements without a named slot attribute; appear in the card body
 * @slot title Title element; takes precedence over title property
 * @slot action Action element
 * @slot subtitle Subtitle element
 * @slot footer Footer element
 */

@register('kat-card')
export class KatalCard extends KatLitElement {
  static get properties() {
    return {
      /**
       * @classprop {string} title Title text that will be displayed to the user. NOTE: title slot takes precedence over title property
       */
      title: { type: String },

      /**
       * @classprop {boolean} no-body-padding Setting to true will remove the default padding in the body and/or default slot
       */
      noBodyPadding: {
        type: Boolean,
        reflect: true,
        attribute: 'no-body-padding',
      },

      /**
       * @classprop {enum} variant Stateful card variant (similar to alert). Attribute is not present by default, but it can explicitly be set to string "default"
       * @enum {value} default No variant (plain card). Property isn't present by default, but it can explicitly be set to string "default"
       * @enum {value} warning Signals moderate issues that may not require immediate attention
       * @enum {value} danger Signals critical issues that likely require immediate attention
       */
      variant: { type: String, reflect: true },
    };
  }

  static get styles() {
    return [baseStyles, cardStyles];
  }

  constructor() {
    super();
    this.noBodyPadding = false;
    this.variant = null;
  }

  firstUpdated() {
    this.observeChildren(this, () => this.requestUpdate());
  }

  render() {
    const result = checkSlots(this);
    const hasHeader = this.title || result.title || result.action;
    const hasFooter = result.footer;

    return html`
      ${!hasHeader
        ? nothing
        : html`
            <div class="header">
              <div class="title">
                <slot name="title">${this.title || nothing}</slot>
              </div>
              <div class="action"><slot name="action"></slot></div>
            </div>
          `}
      <div class="subtitle"><slot name="subtitle"></slot></div>
      <div class="body">
        <slot></slot>
      </div>
      ${!hasFooter
        ? nothing
        : html`<div class="footer">
            <slot name="footer"></slot>
          </div>`}
    `;
  }
}
